import { Avatar, Box, Checkbox, type SxProps, type Theme } from '@mui/material';
import { Image } from '@realadvisor/image';
import { User } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { UserAvatarFragment } from '@graphql/__generated__/graphql';
import { getInitialsFromDisplayName } from '@modules/_shared/utils/contact';

export const USER_AVATAR_FRAGMENT = gql(/* GraphQL */ `
  fragment UserAvatar on users {
    id
    first_name
    last_name
    full_name
    user_images(where: { is_primary: { _eq: true } }, limit: 1) {
      id
      image {
        id
        url
      }
    }
  }
`);

export const COMPACT_USER_AVATAR_SIZE = 28;

export const COMPACT_USER_AVATAR_MIN_WIDTH = COMPACT_USER_AVATAR_SIZE + 8;

type UserAvatarProps = {
  user?:
    | UserAvatarFragment
    | {
        first_name?: string | null;
        last_name?: string | null;
        user_images?: Array<{
          image: { url: string };
        }>;
      }
    | null;
  size?: number;
  onSelect?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selected?: boolean;
  forceCheckbox?: boolean;
  nameFallback?: string;
  sx?: SxProps<Theme>;
};

export const UserAvatar = (props: UserAvatarProps) => {
  const { user, size = 40 } = props;
  const firstName = user?.first_name;
  const lastName = user?.last_name;
  const imageUrl = user?.user_images?.[0]?.image?.url;
  const avatarName =
    `${firstName ?? ''} ${lastName ?? ''}`.replace(' ', '') !== ''
      ? `${firstName} ${lastName}`
      : props.nameFallback;

  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.43,
        cursor: props.onSelect != null ? 'pointer' : 'inherit',
        ...(props.onSelect != null
          ? {
              ['& .MuiCheckbox-root']: {
                visibility: props.forceCheckbox
                  ? 'visible !important'
                  : 'hidden !important',
                opacity: props.forceCheckbox ? '1 !important' : '0 !important',
              },
              ['& .avatar-image']: {
                visibility: !props.forceCheckbox
                  ? 'visible !important'
                  : 'hidden !important',
                opacity: !props.forceCheckbox ? '1 !important' : '0 !important',
              },
              ['&:hover .MuiCheckbox-root']: {
                visibility: 'visible !important',
                opacity: '1 !important',
              },
              ['&:hover .avatar-image']: {
                visibility: 'hidden !important',
                opacity: '0 !important',
              },
            }
          : {}),
        ...(props.sx ?? {}),
      }}
    >
      <Box className="avatar-image">
        {imageUrl != null ? (
          <Image src={imageUrl} srcSize={[[size, size]]} />
        ) : avatarName ? (
          getInitialsFromDisplayName(avatarName)
        ) : (
          <User size={size * 0.6} />
        )}
      </Box>
      {props.onSelect && user != null && 'id' in user && (
        <Checkbox
          onChange={props.onSelect}
          value={user?.id}
          checked={props.selected}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            zIndex: 1,
          }}
          onClick={e => {
            e.stopPropagation();
          }}
        />
      )}
    </Avatar>
  );
};
